import { colors } from '@atlaskit/theme';
// eslint-disable-next-line workspaces/require-dependency
import { loadContentPageStatus } from '@databases/connections-api/actions/load-page-status';
// eslint-disable-next-line workspaces/require-dependency
import { loadContent } from '@databases/connections-api/load-content';
// eslint-disable-next-line workspaces/require-dependency
import {
  selectContentOptional,
  selectContentPropertyOptional
} from '@databases/connections-api/selectors';
// eslint-disable-next-line workspaces/require-dependency
import { connectionsSlice, type FieldValueTypes } from '@databases/connections-api/slice';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type BlogpostReference,
  type ContentState,
  type PageReference
} from '@databases/types/confluence';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext,
  type RawDatabaseValue
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { getConfluencePageAriFromContentRef } from '../../ariHelper.ts';
import {
  addContentState,
  getContentStateKey,
  removeContentState
} from '../../confluence/contentState.ts';
import { getContentKey } from '../../confluence/utils.ts';
import {
  loadContentById,
  resetAvailableContentStates
} from '../../redux/content/actions.ts';
import { selectContentRefOptional } from '../../redux/content/selectors.ts';
import { type SimpleStore } from '../../redux/redux.ts';
import { encodeHtml } from '../../utils.ts';
import { convertType, type FieldType } from '../field-types.ts';
import {
  generateAllValues,
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase
} from '../utils.ts';

import {
  filterValidPageLinkValue,
  isExistingPageLinkValue,
  normalizePageLinkValue
} from './pageLink.ts';

export const DEFAULT_PAGE_STATUS_COLOR = colors.B200;

/**
 * Returns the page link field in the same database that the page status field references.
 */
export function getReferencedPageField({
  database,
  field
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.PAGE_LINK>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.PAGE_LINK) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.PAGE_LINK>;
  }
}

/**
 * Returns the content objects that the referenced page link field references and that are currently loaded into the Redux store.
 */
export function getReferencedStatusContentRefs<State extends DefaultRootState>(
  {
    database,
    field,
    entry
  }: FieldWithContext<DatabaseFieldType, ObjectMode> &
    Optional<EntryWithContext<ObjectMode>, 'entry'>,
  state: State | undefined
): Array<PageReference | BlogpostReference> {
  if (!entry) {
    return [];
  }
  const referencedField = getReferencedPageField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);
  return normalizePageLinkValue(referencedValue?.value ?? [], state).filter(
    (contentRef) => isExistingPageLinkValue(contentRef)
  );
}

/**
 * Returns a list of all content statuses (new and existing ones) currently referenced in the database.
 * @param spaceKey If specified, only content statuses for content objects in the given space are considered
 */
export function getAllContentStates(
  database: Database,
  state: DefaultRootState,
  spaceKey?: string
): Array<Optional<ContentState, 'id'>> {
  const result: Record<string, Optional<ContentState, 'id'>> = {};
  for (const { entry, field, value } of generateAllValues(database)) {
    if (field.type === DatabaseFieldType.PAGE_STATUS) {
      const convertedValue = convertType(
        { database, entry, field, value },
        field.type,
        state
      );
      const contentRefs = getReferencedStatusContentRefs(
        { database, entry, field },
        state
      );
      for (const contentRef of contentRefs) {
        if (!spaceKey || contentRef.spaceKey === spaceKey) {
          const status = getContentStatus(contentRef, convertedValue.value, state);
          if (status) {
            result[getContentStateKey(status)] = status;
          }
        }
      }
    }
  }
  return Object.values(result);
}

/**
 * Returns the new content state id for the content if it is defined in the value, otherwise returns the existing content state.
 */
export function getContentStatus(
  contentRef: PageReference | BlogpostReference,
  value: RawDatabaseValue<DatabaseFieldType.PAGE_STATUS>,
  state: DefaultRootState | undefined
): Optional<ContentState, 'id'> | null | undefined {
  const contentKey = getContentKey(contentRef);
  if (Object.prototype.hasOwnProperty.call(value, contentKey)) {
    return value[contentKey];
  } else {
    const ari = getConfluencePageAriFromContentRef(contentRef);
    if (!state) {
      return;
    }

    const pageStatus = selectContentPropertyOptional(
      state.connections,
      ari || '',
      'pageStatus'
    )?.['data'] as Extract<FieldValueTypes, { type: 'pageStatus' }>['value'] | undefined;

    if (!pageStatus) {
      return;
    }

    return {
      color: pageStatus.color,
      name: pageStatus.text
    };
  }
}

export async function applyPageStatus(
  contentRef: PageReference | BlogpostReference,
  status: Optional<ContentState, 'id'> | null,
  { getState, dispatch }: SimpleStore<DefaultRootState>
) {
  const resolvedContentRef = selectContentRefOptional(getState(), { contentRef });
  if (!resolvedContentRef) {
    return;
  }

  if (status) {
    // Confluence doesn't seem to give too much importance to the content state ID. Here we might submit
    // a content state without an ID that a user has newly created. The newly created content state might
    // have been used in multiple database values by the user, so for each of those values, we will submit
    // a content state with the same name/colour but each without an ID. Confluence is clever enough to
    // detect this and will just create one content state that it will assign to all pages creating it
    // (probably the name is used as the primary key, not the ID).
    await addContentState(resolvedContentRef.contentId, status);
  } else {
    const ari = getConfluencePageAriFromContentRef(contentRef);
    if (ari) {
      dispatch(
        connectionsSlice.actions.removeColumnValueFromContent({
          ari,
          columnName: 'pageStatus'
        })
      );
    }
    await removeContentState(resolvedContentRef.contentId);
  }

  // Changing the content state of a page will almost always change the available content states for the
  // page itself but also for other pages. When a new state is created, it is added to the list of custom
  // states. When an existing custom state is used, it is moved up in the list of suggestions. This is why
  // we reset our cache of available content states.
  dispatch(resetAvailableContentStates());

  await dispatch(loadContentById(resolvedContentRef.contentId, { forceReload: true }));
}

const pageStatusFieldType: FieldType<DatabaseFieldType.PAGE_STATUS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: ({ database, field, entry, value }, state) => {
    const contentRefs = getReferencedStatusContentRefs({ database, field, entry }, state);
    return contentRefs
      .flatMap((contentRef) => {
        const contentStatus = getContentStatus(contentRef, value, state);

        if (contentStatus) {
          return (
            `<span style="display: inline-flex; align-items: center; background: ${colors.N20}; border-radius: 3px; padding: 0 8px; white-space: nowrap">` +
            `<span style="background: ${contentStatus.color}; border-radius: 12px; height: 12px; width: 12px; margin-right: 8px;">&nbsp;</span>` +
            `<span>${encodeHtml(contentStatus.name)}</span>` +
            `</span>`
          );
        } else {
          return `<span>&nbsp;</span>`;
        }
      })
      .join('<br/>');
  },

  parseFromHtml: (el, { field, entry, database }, state) => {
    const contentRefs = getReferencedStatusContentRefs({ database, field, entry }, state);
    return Object.fromEntries(
      contentRefs.map((contentRef) => {
        const existingStatus = getContentStatus(contentRef, {}, state);
        return existingStatus ? [getContentKey(contentRef), existingStatus] : [];
      })
    );
  },

  getInitialValue: () => ({}),

  isEmptyValue: ({ database, field, entry, value }, state) => {
    const allStatuses = getReferencedStatusContentRefs(
      { database, field, entry },
      state
    ).map((contentRef) => getContentStatus(contentRef, value, state));
    return allStatuses.filter((status) => !!status).length === 0;
  },

  isEqualValue: (context1, context2, state) => {
    const contentRefs1 = getReferencedStatusContentRefs(context1, state);
    const contentRefs2 = getReferencedStatusContentRefs(context2, state);
    if (
      !isEqual(
        contentRefs1.map((contentRef) => getContentKey(contentRef)),
        contentRefs2.map((contentRef) => getContentKey(contentRef))
      )
    ) {
      return false;
    }
    const statusKeys1 = contentRefs1.map((contentRef) =>
      getContentStateKey(getContentStatus(contentRef, context1.value, state))
    );
    const statusKeys2 = contentRefs2.map((contentRef) =>
      getContentStateKey(getContentStatus(contentRef, context2.value, state))
    );
    return isEqual(statusKeys1, statusKeys2);
  },

  getStringRepresentation: ({ database, field, entry, value }, state) => {
    if (!state) {
      return '';
    }

    return getReferencedStatusContentRefs({ database, field, entry }, state)
      .flatMap((contentRef) => getContentStatus(contentRef, value, state)?.name ?? '')
      .join('\n');
  },

  loadDependencies: async (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const referencedField = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      return database && getReferencedPageField({ database, field });
    });

    const referencedValue = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      const entry =
        database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
      return (
        database &&
        referencedField &&
        entry &&
        getConvertedDatabaseValue(database, referencedField, entry, state)
      );
    });

    if (referencedValue) {
      for (const contentRef of filterValidPageLinkValue(referencedValue.value)) {
        if (!isExistingPageLinkValue(contentRef) || !contentRef.contentId) {
          continue;
        }

        dispatch(loadContentById(contentRef.contentId));

        const ari = getConfluencePageAriFromContentRef(contentRef);
        if (ari && referencedField) {
          dispatch(
            loadContent({
              ari,
              connectionId: referencedField.id
            })
          );
        }
      }
    }

    const connectionContent = selector((state) => {
      return referencedValue?.value.map((content) => {
        if (referencedValue && 'contentId' in content) {
          const ari = getConfluencePageAriFromContentRef(content);
          if (ari && referencedField) {
            return selectContentOptional(state.connections, ari);
          }
        }

        return null;
      });
    });

    for (const content of connectionContent ?? []) {
      if (content) {
        dispatch(loadContentPageStatus({ ari: content.ari }));
      }
    }

    if (referencedValue) {
      for (const contentRef of filterValidPageLinkValue(referencedValue.value)) {
        if (!isExistingPageLinkValue(contentRef) || !contentRef.contentId) {
          continue;
        }

        dispatch(loadContentById(contentRef.contentId));
      }
    }
  },

  noDefaultValue: true
};

export default pageStatusFieldType;
